// "Revise": not like this — here is what to change, then ask me again. One small sheet with a text box,
// the same on phones and on web. Used by the approval buttons in a room, in a thread and on Home.
import React, { useEffect, useState } from 'react';
import { KeyboardAvoidingView, Modal, Platform, Pressable, StyleSheet, View } from 'react-native';
import { Text, TextInput } from './Text';
import { Btn } from './Btn';
import { makeStyles, radius, useTheme } from '../theme';

export function ReviseSheet({ visible, about, onCancel, onSend }: {
  visible: boolean;
  /** What is being sent back, in a few words: shown under the title so it is clear which request this is. */
  about?: string;
  onCancel: () => void;
  onSend: (note: string) => void;
}) {
  const theme = useTheme(); const s = useStyles();
  const [note, setNote] = useState('');
  useEffect(() => { if (visible) setNote(''); }, [visible]);
  const ready = note.trim().length > 0;
  const send = () => { if (ready) onSend(note.trim()); };
  return (
    // No fade on web: the browser only removes a faded sheet when the animation ends, and a tab that is not
    // being drawn (in the background, or minimised) never ends it, which left the sheet on screen.
    <Modal visible={visible} transparent animationType={Platform.OS === 'web' ? 'none' : 'fade'} onRequestClose={onCancel} statusBarTranslucent>
      <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'padding' : undefined} style={s.backdrop}>
        <Pressable style={StyleSheet.absoluteFill} onPress={onCancel} accessibilityLabel="Close" />
        <View style={s.card} accessibilityViewIsModal>
          <Text style={s.title}>What should change?</Text>
          {!!about && <Text style={s.about} numberOfLines={2}>{about}</Text>}
          <TextInput
            style={s.input} value={note} onChangeText={setNote} autoFocus multiline
            placeholder="For example: the tone is too formal" placeholderTextColor={theme.faint}
            accessibilityLabel="What should change"
            // Web: Enter is a new line here (a note can be long); Ctrl+Enter or Cmd+Enter sends.
            onKeyPress={Platform.OS !== 'web' ? undefined : (e) => {
              const k = e.nativeEvent as unknown as { key?: string; ctrlKey?: boolean; metaKey?: boolean };
              if (k.key === 'Enter' && (k.ctrlKey || k.metaKey)) { e.preventDefault(); send(); }
            }}
          />
          <Text style={s.hint}>It is not approved. The agent changes it and asks you again.</Text>
          <View style={s.row}>
            <Btn label="Cancel" kind="ghost" onPress={onCancel} />
            <Btn label="Send back" onPress={send} disabled={!ready} />
          </View>
        </View>
      </KeyboardAvoidingView>
    </Modal>
  );
}

const useStyles = makeStyles((theme) => StyleSheet.create({
  backdrop: { flex: 1, backgroundColor: theme.backdrop, alignItems: 'center', justifyContent: 'center', padding: 16 },
  card: { width: '100%', maxWidth: 440, backgroundColor: theme.surface, borderRadius: radius.lg, borderWidth: 1, borderColor: theme.border, padding: 20 },
  title: { color: theme.ink, fontSize: 17, fontWeight: '800' },
  about: { color: theme.muted, fontSize: 13.5, marginTop: 4 },
  input: { marginTop: 14, minHeight: 96, maxHeight: 220, backgroundColor: theme.surface2, borderRadius: radius.md, borderWidth: 1, borderColor: theme.border, color: theme.ink, fontSize: 15, lineHeight: 21, padding: 12, textAlignVertical: 'top' },
  hint: { color: theme.faint, fontSize: 12.5, marginTop: 8 },
  row: { flexDirection: 'row', justifyContent: 'flex-end', gap: 10, marginTop: 18 },
}));
